/**
 * HOME OFICIAL — venda aberta (30/09/2026). Cor: magenta, escolhida pelo marketing em 01/10 (a comparação entre
 * sete prévias acabou). A home da pré-venda (`home-page.tsx`) ficou só em `/founding`.
 *
 * O que vem de onde (decisão do dono, 30/09):
 * - HERO: o nosso — mesma rolagem presa e animações; textos do marketing; a foto clara da estação no lugar
 *   do vídeo escuro (o vídeo destoava da página clara).
 * - Seções, textos e posição das imagens: o HTML do marketing (texto sem alteração).
 * - Faixa de parceiros: mantida, com os logos em cinza escuro (fundo claro).
 * - Oferta e botões "Assinar": a OFERTA PRINCIPAL marcada na plataforma (`useOfertaPrincipal`), nada fixo.
 * - FAQ: o nosso modelo, sem as perguntas de pré-venda, somado às do marketing. Rodapé: o nosso.
 * - Fora: Founding, lote, vagas, contador, ativação em ondas e o card de acesso antecipado.
 *
 * Fundo claro; a cor de destaque mora nas variáveis de `.hto` (home-oficial.css).
 */
import { useEffect, useRef, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { useSmoothScroll } from "@/hooks/use-smooth-scroll";
import { CustomCursor, Reveal, Footer } from "@/components/site/shared";
import { FaixaParceiros } from "@/components/site/faixa-parceiros";
import { reais, useOfertaPrincipal } from "@/lib/oferta-principal";
import "@/styles/landing-v2.css";
import "@/styles/hts-extra.css";
import "@/styles/home-oficial.css";


/** Imagens da identidade HTS (Drive: Midia/Identidade HTS/imagens site), convertidas para webp 1672px. */
const IMG = {
  estacao: "/img/landing/estacao-vista-urbana.webp",
  graficoDi: "/img/landing/grafico-e-estrutura-di.webp",
  estruturaDi: "/img/landing/estrutura-di.webp",
  mobile: "/img/landing/cel-e-ipad.webp",
  diario: "/img/landing/relatorio-de-trades.webp",
  juros: "/img/landing/juros-globais.webp",
};

const NAV = [
  { id: "porque", label: "Por que HTS" },
  { id: "plataforma", label: "Plataforma" },
  { id: "recursos", label: "Funcionalidades" },
  { id: "profissional", label: "Profissional" },
];
const irPara = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

/** Barra do topo. A página de parceiro troca o nome ao lado da marca e o botão (01/10). */
export function Topbar({ destino, rotulo, cta = "Assinar HTS", semNav = false }: { destino: string; rotulo?: string; cta?: string; semNav?: boolean }) {
  const [rolou, setRolou] = useState(false);
  useEffect(() => {
    const f = () => setRolou(window.scrollY > 60);
    window.addEventListener("scroll", f, { passive: true });
    return () => window.removeEventListener("scroll", f);
  }, []);
  return (
    <header className={`lv2-topbar ${rolou ? "is-scrolled" : ""}`}>
      <a href="/" className="lv2-topbar-logo"><span className="hts-wordmark">HTS</span>{rotulo ? <span className="hto-topbar-rotulo">{rotulo}</span> : null}</a>
      <nav className="lv2-topbar-nav">
        {!semNav && NAV.map((l) => <button key={l.id} className="nav-link" onClick={() => irPara(l.id)}>{l.label}</button>)}
        {!semNav && <a href="/recursos/" className="nav-link">Recursos</a>}
        <a href={destino} className="cta">{cta}</a>
      </nav>
    </header>
  );
}

/** O NOSSO hero (vídeo, rolagem presa, fade) com os textos do marketing. */
function Hero({ destino }: { destino: string }) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const wrapper = wrapperRef.current, content = contentRef.current;
    if (!wrapper || !content) return;
    const f = () => {
      const rect = wrapper.getBoundingClientRect();
      const max = wrapper.offsetHeight - window.innerHeight;
      const p = Math.max(0, Math.min(1, -rect.top / Math.max(1, max)));
      content.style.opacity = String(Math.max(0, 1 - p * 1.4));
      content.style.transform = `translateY(${p * -40}px)`;
    };
    window.addEventListener("scroll", f, { passive: true });
    f();
    return () => window.removeEventListener("scroll", f);
  }, []);
  return (
    <div ref={wrapperRef} className="lv2-hero-scroll">
      <section className="lv2-hero">
        {/* Foto clara da estação no lugar do vídeo escuro (dono, 30/09: "muito escuro e destoa da página");
            o véu branco à esquerda segura a leitura do texto. Rolagem presa e fade continuam os nossos. */}
        <div className="hto-hero-foto" style={{ backgroundImage: `url(${IMG.estacao})` }} />
        <div className="hto-hero-veu" />
        <div ref={contentRef} className="lv2-hero-content">
          <div className="lv2-hero-meta">
            <div><span>HTS — HIGH TRADING SYSTEM</span><span>Plataforma HTS</span></div>
          </div>
          <h1 className="lv2-display hts-hero-h1">
            Tudo o que o trader precisa,
            <br />
            <span className="lv2-outlined">em um só lugar.</span>
          </h1>
          <p className="hto-hero-lead">
            Gráficos, book de ofertas, Estrutura DI, fluxo, contexto de mercado, referências globais e
            ambiente operacional em uma experiência integrada.
            <br />
            <em className="hto-hero-nota">
              A HTS organiza informação e contexto. Operações no mercado envolvem risco e decisões são de responsabilidade do operador.
            </em>
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <a href={destino} className="hts-hero-cta" data-cursor-hover>Quero assinar a HTS <ArrowUpRight size={18} /></a>
            <button onClick={() => irPara("plataforma")} className="hts-hero-cta is-ghost" data-cursor-hover>Conhecer a plataforma</button>
          </div>
        </div>
        <div className="lv2-hero-scrollhint">Role para continuar</div>
      </section>
    </div>
  );
}

export const Rotulo = ({ children }: { children: React.ReactNode }) => <span className="hto-lab">{children}</span>;

function Oferta({ destino }: { destino: string }) {
  const { oferta, carregando } = useOfertaPrincipal();
  if (carregando || !oferta) return null; // sem oferta principal no ar: a seção some, nada de preço inventado
  const temDe = oferta.fullPriceCents != null && oferta.fullPriceCents > oferta.priceCents;
  return (
    <section className="hto-sec hto-oferta" id="oferta">
      <div className="hto-c">
        <Reveal>
          <div className="hto-ob">
            <div>
              <Rotulo>Acesso HTS</Rotulo>
              <h2 className="hto-ob-h">Mais informação. Mais contexto. Mais inteligência.</h2>
              <p>A HTS foi criada para reunir as principais camadas da leitura de mercado dentro de uma experiência profissional, visual e integrada.</p>
              <p><b>Sem formulário. Sem esperar contato.</b> Clique no botão e vá direto para o checkout.</p>
              <div className="hto-mins">
                {["Mercado brasileiro + global", "Estrutura DI e contexto", "Ambiente operacional", "Experiência desktop + mobile"].map((m) => <div key={m} className="hto-min">✓ {m}</div>)}
              </div>
            </div>
            <div className="hto-price">
              <div className="hto-lab hto-lab-claro">Condição de acesso</div>
              {temDe && <div className="hto-old">Valor de referência: {reais(oferta.fullPriceCents!)}/mês</div>}
              <div className="hto-money">{reais(oferta.priceCents)}<span>/mês</span></div>
              <p>
                {oferta.trialDays > 0 ? `${oferta.trialDays} dias grátis para começar; a primeira cobrança só no fim do teste. ` : ""}
                Consulte no checkout os termos, disponibilidade e condições vigentes.
              </p>
              <a className="hto-btn hto-btn-branco" href={destino}>QUERO ASSINAR A HTS →</a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/** Nosso FAQ (sem pré-venda) + as do marketing, no nosso modelo de acordeão. */
const FAQ = [
  { q: "O que é a HTS?", a: "É uma plataforma de análise e ambiente operacional que reúne diferentes informações de mercado em uma experiência integrada." },
  { q: "Já tenho plataforma e gráfico. Por que a HTS?", a: "Porque a HTS não compete em ter mais um indicador. Ela conecta a execução com o que está formando o preço — juros, dólar global, política monetária, risco e notícia — e depois transforma cada operação em diário e métricas. Não é mais uma tela: é o processo de decisão inteiro no mesmo ambiente." },
  { q: "A HTS substitui a tomada de decisão do operador?", a: "Não. A plataforma organiza dados, contexto e recursos. A leitura e as decisões continuam sendo responsabilidade do operador." },
  { q: "Posso acompanhar referências brasileiras e internacionais?", a: "A proposta da HTS é aproximar informações do mercado brasileiro e referências globais dentro da mesma rotina de análise." },
  { q: "“Contexto antes da decisão” é informação privilegiada?", a: "Não. É contexto público: calendário, expectativa de mercado, curva de juros, dólar, risco e política monetária — tudo o que já está disponível antes da decisão, organizado num lugar só em vez de espalhado em cinco ferramentas. A HTS também não dá call nem diz onde comprar ou vender." },
  { q: "Isso é uma corretora? Preciso trocar a minha?", a: "Não. A HTS é plataforma de análise e operação. Sua conta e sua custódia continuam onde estão." },
  { q: "Roda em Mac? E no celular?", a: "Windows, Mac e navegador. No celular funciona como webapp; aplicativos nas lojas estão no roadmap." },
  { q: "Preciso saber programar?", a: "Não. Todas as ferramentas vêm prontas, cada uma com vídeo de uso." },
  { q: "Tenho uma mesa proprietária (ou quero montar). Dá pra usar a HTS?", a: "Dá. A mesa é um módulo à parte, em white label: mandato, limites e monitor de risco, fases, trilha, extrato, saque e backoffice, com a sua marca e cobrança por assento. Fale com o time por hts@hightrading.com.br." },
  { q: "Quem está por trás?", a: "A HTS é desenvolvida e operada pela High Trading LTDA e roda em produção desde 2021, hoje com 535 operadores profissionais em estrutura institucional." },
  { q: "Como faço para assinar?", a: "Use qualquer botão “Assinar HTS” desta página. Você será direcionado diretamente para o checkout." },
];

export function Faq() {
  const [aberta, setAberta] = useState<number | null>(0);
  return (
    <section className="hto-sec" id="duvidas">
      <div className="hto-c hto-faqg">
        <div>
          <Rotulo>Dúvidas frequentes</Rotulo>
          <h2 className="hto-title">Antes de acessar a plataforma.</h2>
          <p className="hto-sub">Informações essenciais sobre a proposta e o uso da HTS.</p>
        </div>
        <div className="lv2-faq-list hto-faq">
          {FAQ.map((item, i) => {
            const open = aberta === i;
            return (
              <div key={item.q} className={`lv2-faq-item ${open ? "is-open" : ""}`}>
                <button type="button" className="lv2-faq-q" data-cursor-hover onClick={() => setAberta(open ? null : i)}>
                  <span style={{ display: "flex", alignItems: "center", flex: 1 }}>
                    <span className="lv2-faq-q-num">{String(i + 1).padStart(2, "0")}</span>
                    <span>{item.q}</span>
                  </span>
                  <span className="lv2-faq-q-icon" aria-hidden="true" />
                </button>
                <div className="lv2-faq-a"><p style={{ margin: 0 }}>{item.a}</p></div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/** O meio da home (do "por que" ao banner final): a página de parceiro usa igual (01/10). */
export function SecoesDaPlataforma({ destino }: { destino: string }) {
  return (
    <>
      <section className="hto-sec hto-soft" id="porque">
        <div className="hto-c hto-two">
          <Reveal>
            <Rotulo>O problema não é falta de gráfico</Rotulo>
            <h2 className="hto-title">O gráfico mostra o movimento. Mas não conta a história inteira.</h2>
            <p className="hto-sub">Quando o operador olha apenas candle, indicador e gatilho, ele enxerga a consequência. A leitura começa antes: expectativa, juros, fluxo, risco, moedas, macroeconomia e ativos que se relacionam entre si.</p>
            <div className="hto-bul">
              {[["01", "Antes da abertura", "Entenda o ambiente que pode influenciar o pregão antes de tomar a primeira decisão."],
                ["02", "Durante o movimento", "Acompanhe o que mudou nas referências e o que o mercado começou a reprificar."],
                ["03", "Na hora do clique", "Use tecnologia para organizar contexto sem tirar o operador do centro da decisão."]].map(([n, t, d]) => (
                <div key={n} className="hto-row"><div className="hto-num">{n}</div><div><h3>{t}</h3><p>{d}</p></div></div>
              ))}
            </div>
          </Reveal>
          <div className="hto-visual" style={{ backgroundImage: `url(${IMG.estacao})` }}>
            <div className="hto-vt"><small>Visão integrada</small><strong>Menos telas fragmentadas. Mais leitura do cenário dentro da mesma estação.</strong></div>
          </div>
        </div>
      </section>

      <section className="hto-sec" id="plataforma">
        <div className="hto-c">
          <div className="hto-head hto-center">
            <Rotulo>Plataforma HTS</Rotulo>
            <h2 className="hto-title">Tudo o que o trader precisa acompanhar, em um só ambiente.</h2>
            <p className="hto-sub">A plataforma foi desenhada para transformar diferentes camadas de informação em uma experiência visual, clara e operacional.</p>
          </div>
          <Reveal stagger>
            <div className="hto-cards">
              {[{ img: IMG.graficoDi, pos: "center", ico: "▥", t: "Gráficos e Book de Ofertas", d: "Acompanhe o mercado com gráficos, book, times & trades e indicadores em uma interface rápida e organizada.", l: "Conhecer gráficos →" },
                { img: IMG.estruturaDi, pos: "center", ico: "◎", t: "Estrutura DI e Fluxo", d: "Visualize a estrutura de juros, inclinações, spreads e alterações de precificação para enriquecer a leitura do ambiente.", l: "Explorar Estrutura DI →" },
                // 30/09: "Automação e Controle Mobile" saiu (ainda não existe); entrou o Diário de Trade.
                { img: IMG.diario, pos: "center", ico: "◧", t: "Diário de Trade e Performance", d: "Cada operação vira registro, métrica e padrão de comportamento: o que você fez, quanto custou e o que treinar no próximo pregão.", l: "Conhecer o Diário de Trade →", href: "/recursos/diario-de-trade" }].map((c) => (
                <article key={c.t} className="hto-card">
                  <div className="hto-ci" style={{ backgroundImage: `url(${c.img})`, backgroundPosition: c.pos }} />
                  <div className="hto-cb">
                    <div className="hto-ico">{c.ico}</div>
                    <h3>{c.t}</h3>
                    <p>{c.d}</p>
                    {"href" in c ? <a className="hto-link" href={c.href}>{c.l}</a> : <button className="hto-link" onClick={() => irPara("recursos")}>{c.l}</button>}
                  </div>
                </article>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="hto-sec hto-soft" id="recursos">
        <div className="hto-c">
          <div className="hto-head">
            <Rotulo>Recursos da plataforma</Rotulo>
            <h2 className="hto-title">Tecnologia profissional. Experiência simples de usar.</h2>
            <p className="hto-sub">A HTS reúne diferentes camadas de informação para que você não precise construir sua leitura pulando de ferramenta em ferramenta.</p>
          </div>
          <Reveal stagger>
            <div className="hto-res">
              {[["B3", "Mercado brasileiro", "Índice, dólar, juros, ações e referências da B3 reunidas dentro da mesma estação."],
                ["GL", "Mercado internacional", "S&P 500, Nasdaq, DXY, Treasuries, moedas e ativos que influenciam o fluxo global."],
                ["DI", "Curva de juros", "Visualize estrutura, inclinação e mudanças de precificação para interpretar o ambiente."],
                ["N", "Notícias + calendário", "Agenda macro e informações relevantes próximas da tela em que você acompanha o mercado."],
                ["Q", "Research quantitativo", "Regime, risco, valuation, sentimento e outras variáveis organizadas para leitura."],
                ["OP", "Ambiente operacional", "Book, telas operacionais e recursos integrados à experiência da plataforma."]].map(([i, t, d]) => (
                <div key={t} className="hto-r"><div className="hto-ri">{i}</div><h3>{t}</h3><p>{d}</p></div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="hto-sec hto-escuro" id="profissional">
        <div className="hto-c hto-bg">
          <div className="hto-bv" style={{ backgroundImage: `url(${IMG.diario})` }}>
            <div className="hto-cap"><strong>Mercado é informação conectada.</strong>A proposta da HTS é deixar essas relações mais próximas da sua tomada de decisão.</div>
          </div>
          <Reveal>
            <Rotulo>Uma leitura mais completa</Rotulo>
            <h2 className="hto-title">Mercado é feito por pessoas tomando decisões sob pressão.</h2>
            <p className="hto-sub">Por isso, uma plataforma não deve apenas exibir números. Ela precisa organizar informação de forma visual, clara e operacional.</p>
            <div className="hto-checks">
              {[["Contexto antes do clique.", "Comece pelo ambiente e não apenas pelo gatilho visual."],
                ["Expectativa versus realizado.", "Acompanhe o que o mercado já precificava."],
                ["Relações entre ativos.", "Observe juros, moedas, risco e referências globais."],
                ["Rotina centralizada.", "Menos tempo alternando ferramentas e mais tempo interpretando o cenário."]].map(([b, t]) => (
                <div key={b} className="hto-check"><i>✓</i><span><b>{b}</b> {t}</span></div>
              ))}
            </div>
            <a className="hto-btn hto-pri" href={destino} style={{ marginTop: 25 }}>Quero acessar a HTS →</a>
          </Reveal>
        </div>
      </section>

      <section className="hto-sec hto-soft">
        <div className="hto-c">
          <div className="hto-head">
            <Rotulo>Mudança de postura</Rotulo>
            <h2 className="hto-title">O mesmo botão. O mesmo mercado. Uma preparação completamente diferente.</h2>
            <p className="hto-sub">A diferença está menos no clique e mais no que o operador entende antes dele.</p>
          </div>
          <div className="hto-compare">
            <div className="hto-cc">
              <div className="hto-top">Leitura reativa</div>
              <h3>Descobre depois que o preço já andou.</h3>
              {[["Começa pelo candle.", "Tenta encontrar o motivo somente depois do movimento."], ["Opera a manchete.", "Sem saber o que o mercado já esperava."],
                ["Analisa o ativo isolado.", "Ignora referências que podem estar puxando o movimento."], ["Abre várias ferramentas.", "Mais fragmentação e mais ruído na rotina."]].map(([b, t]) => (
                <div key={b} className="hto-item"><span className="hto-mk">×</span><span><b>{b}</b><br />{t}</span></div>
              ))}
            </div>
            <div className="hto-cc hto-good">
              <div className="hto-top">Leitura profissional</div>
              <h3>Entende o ambiente antes de executar.</h3>
              {[["Começa pelo contexto.", "Entende ambiente, referências e variáveis relevantes."], ["Acompanha expectativa.", "Compara realizado, esperado e precificado."],
                ["Lê relações.", "Juros, moedas, risco e ativos entram na análise."], ["Centraliza a rotina.", "Mais informação dentro de um ambiente integrado."]].map(([b, t]) => (
                <div key={b} className="hto-item"><span className="hto-mk">✓</span><span><b>{b}</b><br />{t}</span></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="hto-banner" style={{ ["--hto-banner" as string]: `url(${IMG.juros})` }}>
        <div className="hto-c">
          <div className="hto-bc">
            <Rotulo>High Trading System</Rotulo>
            <h2>Se o mercado não opera isolado, sua análise também não deveria.</h2>
            <p>Leve mais contexto para a sua rotina operacional com uma estação construída para conectar as principais informações que você acompanha todos os dias.</p>
            <a className="hto-btn hto-pri" href={destino}>Assinar HTS agora →</a>
          </div>
        </div>
      </section>
    </>
  );
}

export default function HomeOficialPage() {
  useSmoothScroll();
  const { destino } = useOfertaPrincipal();
  return (
    <div className="lv2 hts-compact-display hto">
      <CustomCursor />
      <Topbar destino={destino} />
      <Hero destino={destino} />

      <div className="hto-faixa"><FaixaParceiros /></div>

      <div className="hto-stats">
        <div className="hto-c hto-sg">
          {[["B3", "Mercado brasileiro"], ["Global", "Referências internacionais"], ["DI", "Curva de juros"], ["1 estação", "Contexto + operação"]].map(([v, l]) => (
            <div key={v} className="hto-stat"><strong>{v}</strong><span>{l}</span></div>
          ))}
        </div>
      </div>

      <SecoesDaPlataforma destino={destino} />

      <Oferta destino={destino} />
      <Faq />

      <Footer
        navLinks={[...NAV.map((l) => ({ href: `#${l.id}`, label: l.label })), { href: "/recursos/", label: "Recursos" }]}
        rodape="Plataforma de análise e operação · Não é corretora"
      />
    </div>
  );
}
